// app/src/features/chat/useChatComposer.js
import { useEffect, useState } from "react";

/**
 * The composer's text (`input` / `setInput`) and the type-anywhere focus: a printable key pressed
 * outside a text field, with no overlay open and the chat panel open, lands in the composer and
 * focuses it. Called where `AppInner` ran the focus effect (after the five overlay bindings it
 * reads); the `useState` registers there too — no reader of `input` sits between where it was
 * declared and the effect. The effect's dependency array is BASE's (`setInput` is this hook's own
 * setter, a sibling as it was in `AppInner`).
 *
 * @param {object} deps
 * @param {boolean} deps.chatOpen  the chat panel is open (useLayoutShell)
 * @param {object|null} deps.selEvt  the selected timeline event (an open overlay)
 * @param {boolean} deps.bugOpen  the bug report panel is open
 * @param {object|null} deps.pendingPreprocess  the preprocess panel is open
 * @param {boolean} deps.showReport  the report modal is open
 * @param {object|null} deps.customEvtPopover  the custom-trigger popover is open
 * @returns {{ input: string, setInput: Function }}
 */
export function useChatComposer({
  chatOpen,
  selEvt,
  bugOpen,
  pendingPreprocess,
  showReport,
  customEvtPopover,
}) {
  const [input, setInput] = useState("");

  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (!e.key || e.key.length !== 1) return;
      const ae = document.activeElement;
      if (ae) {
        const tag = ae.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || ae.isContentEditable) return;
      }
      if (selEvt || bugOpen || pendingPreprocess || showReport || customEvtPopover) return;
      if (!chatOpen) return;
      const ta = document.querySelector("[data-chat-input]");
      if (!ta) return;
      e.preventDefault();
      setInput((prev) => prev + e.key);
      ta.focus();
      requestAnimationFrame(() => {
        try {
          const v = ta.value;
          ta.setSelectionRange(v.length, v.length);
        } catch (_) {
          /*ignore*/
        }
      });
    };
    // telemetry-gesture: type-anywhere focuses the composer; chat.ask starts on send
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [chatOpen, selEvt, bugOpen, pendingPreprocess, showReport, customEvtPopover]);

  return { input, setInput };
}
